/**
 * providers/mockProviders.ts — placeholder implementations of every provider
 * contract. Deterministic, offline, zero-network. Each one is registered as
 * the default in the ProviderRegistry and is replaced simply by registering a
 * real integration (Google Places, OpenWeather, …) under the same capability.
 */

import type { RoamerLocation } from '@/services/RoamerLocationEngine';
import type { CityMetrics } from '../types';
import { ROAMER_SCORE_CATEGORIES } from '../types';
import type {
  BookingProvider,
  BookingQuery,
  CityMetricsProvider,
  CommunityReview,
  CommunityReviewProvider,
  ExchangeRateProvider,
  ExchangeRateSnapshot,
  HousingProvider,
  HousingQuery,
  InternetQualityProvider,
  InternetQualitySnapshot,
  PlacesProvider,
  PlacesQuery,
  TravelAdvisoryProvider,
  TravelAdvisorySnapshot,
  WeatherProvider,
  WeatherSnapshot,
} from './types';
import {
  between,
  chance,
  generatePlaceholderRecommendations,
  intBetween,
  localDayIndex,
  pick,
  seededRng,
} from './mockData';

// ─── Places / Housing / Booking ───────────────────────────────────────────────

export const mockPlacesProvider: PlacesProvider = {
  meta: { id: 'mock-places', name: 'Placeholder Places', capability: 'places', live: false },
  async getPlaces(q: PlacesQuery) {
    return generatePlaceholderRecommendations(q.location, q.category, q.limit, this.meta.id);
  },
};

export const mockHousingProvider: HousingProvider = {
  meta: { id: 'mock-housing', name: 'Placeholder Housing', capability: 'housing', live: false },
  async getListings(q: HousingQuery) {
    let listings = generatePlaceholderRecommendations(q.location, 'housing', q.limit * 2, this.meta.id);
    if (q.maxMonthlyUsd != null) {
      // Soft cap: keep some slightly-over options so ranking can explain trade-offs.
      const cap = q.maxMonthlyUsd * 1.35;
      listings = listings.filter((l) => (l.attributes.monthlyCost ?? 0) <= cap);
    }
    return listings.slice(0, q.limit);
  },
};

export const mockBookingProvider: BookingProvider = {
  meta: { id: 'mock-booking', name: 'Placeholder Stays', capability: 'booking', live: false },
  async getStays(q: BookingQuery) {
    return generatePlaceholderRecommendations(q.location, 'housing', q.limit, this.meta.id)
      .map((r) => ({ ...r, tags: [...r.tags, 'Short Stay'] }));
  },
};

// ─── Weather ──────────────────────────────────────────────────────────────────

export const mockWeatherProvider: WeatherProvider = {
  meta: { id: 'mock-weather', name: 'Placeholder Weather', capability: 'weather', live: false },
  async getSnapshot(location: RoamerLocation, now: number): Promise<WeatherSnapshot> {
    const rng = seededRng(`${location.locationId}:weather:${localDayIndex(location, now)}`);
    // Latitude drives the placeholder climate band — generic, not per-city.
    const tropical = Math.abs(location.latitude) < 25;
    const baseTemp = tropical ? between(rng, 26, 34) : between(rng, 8, 26);
    const willRain = chance(rng, tropical ? 0.45 : 0.25);
    const condition = willRain
      ? (chance(rng, 0.25) ? 'storm' as const : 'rain' as const)
      : pick(rng, ['clear', 'partly-cloudy', 'cloudy'] as const);
    return {
      tempC: Math.round(baseTemp),
      highC: Math.round(baseTemp + between(rng, 1, 4)),
      lowC: Math.round(baseTemp - between(rng, 3, 7)),
      humidityPct: intBetween(rng, tropical ? 55 : 35, tropical ? 90 : 70),
      condition,
      rainStartHour: willRain ? intBetween(rng, 12, 20) : null,
    };
  },
};

// ─── Exchange rate ────────────────────────────────────────────────────────────

export const mockExchangeRateProvider: ExchangeRateProvider = {
  meta: { id: 'mock-fx', name: 'Placeholder FX', capability: 'exchangeRate', live: false },
  async getSnapshot(location: RoamerLocation, now: number): Promise<ExchangeRateSnapshot> {
    const rng = seededRng(`${location.locationId}:fx:${localDayIndex(location, now)}`);
    const changePct = Math.round(between(rng, -1.4, 1.4) * 100) / 100;
    return {
      baseCurrency: 'USD',
      quoteCurrency: 'LOCAL', // real FX provider fills the ISO code
      rate: Math.round(between(rng, 0.8, 40) * 100) / 100,
      trend: changePct > 0.25 ? 'improved' : changePct < -0.25 ? 'worsened' : 'flat',
      changePct,
    };
  },
};

// ─── Internet quality ─────────────────────────────────────────────────────────

export const mockInternetQualityProvider: InternetQualityProvider = {
  meta: { id: 'mock-net', name: 'Placeholder Internet Quality', capability: 'internetQuality', live: false },
  async getSnapshot(location: RoamerLocation, now: number): Promise<InternetQualitySnapshot> {
    const cityRng = seededRng(`${location.locationId}:net-base`);
    const dayRng = seededRng(`${location.locationId}:net:${localDayIndex(location, now)}`);
    const normal = between(cityRng, 40, 320);
    const today = normal * between(dayRng, 0.75, 1.25);
    return {
      avgDownloadMbps: Math.round(today),
      avgUploadMbps: Math.round(today * between(dayRng, 0.3, 0.7)),
      reliabilityPct: intBetween(dayRng, 88, 100),
      todayVsNormal: today > normal * 1.12 ? 'better' : today < normal * 0.88 ? 'worse' : 'normal',
    };
  },
};

// ─── Travel advisory ──────────────────────────────────────────────────────────

const ADVISORY_SUMMARY: Record<1 | 2 | 3 | 4, string> = {
  1: 'Exercise normal precautions.',
  2: 'Exercise increased caution.',
  3: 'Reconsider non-essential travel.',
  4: 'Do not travel.',
};

export const mockTravelAdvisoryProvider: TravelAdvisoryProvider = {
  meta: { id: 'mock-advisory', name: 'Placeholder Travel Advisory', capability: 'travelAdvisory', live: false },
  async getAdvisory(location: RoamerLocation): Promise<TravelAdvisorySnapshot> {
    const rng = seededRng(`${location.locationId}:advisory`);
    // Placeholder skews benign — a real provider reads government feeds.
    const level = (chance(rng, 0.8) ? 1 : 2) as 1 | 2;
    return { level, summary: ADVISORY_SUMMARY[level] };
  },
};

// ─── Community reviews ────────────────────────────────────────────────────────

const REVIEW_SNIPPETS = [
  'Great WiFi and easy to focus here.',
  'Friendly staff, fair prices for the area.',
  'Gets busy after lunch — mornings are best.',
  'Comfortable for long work sessions.',
  'Would come back. Solid all-rounder.',
  'A bit noisy on weekends but otherwise great.',
];

const REVIEW_AUTHORS = ['Alex M.', 'Priya S.', 'Jonas K.', 'Maya R.', 'Diego F.', 'Sam T.'];

export const mockCommunityReviewProvider: CommunityReviewProvider = {
  meta: { id: 'mock-reviews', name: 'Placeholder Community Reviews', capability: 'communityReviews', live: false },
  async getReviews(placeId: string, limit: number): Promise<CommunityReview[]> {
    const out: CommunityReview[] = [];
    for (let i = 0; i < limit; i++) {
      const rng = seededRng(`${placeId}:review:${i}`);
      out.push({
        id: `${placeId}:review:${i}`,
        placeId,
        author: pick(rng, REVIEW_AUTHORS),
        rating: Math.round(between(rng, 3, 5) * 2) / 2,
        text: pick(rng, REVIEW_SNIPPETS),
        createdAt: 0, // placeholder — real providers return actual timestamps
      });
    }
    return out;
  },
};

// ─── City metrics ─────────────────────────────────────────────────────────────

export const mockCityMetricsProvider: CityMetricsProvider = {
  meta: { id: 'mock-city-metrics', name: 'Placeholder City Metrics', capability: 'cityMetrics', live: false },
  async getMetrics(location: RoamerLocation): Promise<CityMetrics> {
    const metrics = {} as CityMetrics;
    for (const category of ROAMER_SCORE_CATEGORIES) {
      const rng = seededRng(`${location.locationId}:metric:${category}`);
      metrics[category] = intBetween(rng, 55, 97);
    }
    return metrics;
  },
};
